import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import UpdateApplicationRequest from '../../../../../shared/models/applications/api/UpdateApplicationRequest';
import { TelephonyDeploymentResponse } from '../../../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';
import {
    findSparkVoiceOptionByVoice,
    getSparkVoiceOption,
    getSparkVoiceString,
    SparkVoiceOption
} from '../../../../../shared/constants/sparkConstants/voiceOptions';
import { carryVoiceStringPrompt, getVoiceStringPrompt, setVoiceStringPrompt, supportsVoicePrompt } from '../../../../../shared/utils/ttsVoiceString';
import { FoodOrderingGenAIModelSettingId, FoodOrderingGenAIPromptOverrideSettingId } from '../../../../../shared/constants/featureFlagSettingIds';
import { GenAIFoodOrdering } from '../../../../../shared/constants/featureFlagIds';

const getVoiceLanguage = (locale?: string): 'english' | 'spanish' => (
    (locale || '').toLowerCase().startsWith('es') ? 'spanish' : 'english'
);

const resolveCatalogVoiceOption = (value?: string): SparkVoiceOption | undefined => {
    if (!value) return undefined;

    return getSparkVoiceOption(value) || findSparkVoiceOptionByVoice(value);
};

const resolveVoiceForDeployment = (
    option: SparkVoiceOption,
    deployment: TelephonyDeploymentResponse
): string => (
    getSparkVoiceString(option, getVoiceLanguage(deployment.locale)) || option.englishVoice
);

export const buildTelephonyUpdateRequest = (
    editingDeployment: TelephonyDeploymentResponse,
    editType: string,
    editValues: { editAsrProvider?: string, editProactivePrompt?: string, editVoiceString?: string, editVoiceProvider?: string }
): any => {
    let request: any = { ...editingDeployment };

    if (editType === 'asr') {
        request.asrProvider = editValues.editAsrProvider;
    } else if (editType === 'proactive') {
        request.generativeProactiveResponsePrompt = editValues.editProactivePrompt;
        request.useGenerativeProactiveResponse = true;
        request.useProactiveResponses = true;
    } else if (editType === 'voice') {
        // With an explicit provider the voice string is saved exactly as picked.
        const standardOption = editValues.editVoiceProvider ? undefined : resolveCatalogVoiceOption(editValues.editVoiceString);
        const provider = editValues.editVoiceProvider || (standardOption ? standardOption.provider : 'ElevenLabs');
        const voiceString = standardOption
            ? carryVoiceStringPrompt(resolveVoiceForDeployment(standardOption, editingDeployment), getVoiceStringPrompt(editValues.editVoiceString))
            : editValues.editVoiceString;

        request.ttsProvider = provider;
        request.ttsVoice = voiceString;
        request.ttsOptions = [{ ttsProvider: provider, ttsVoice: voiceString }];
    }

    return request;
};

/**
 * A bulk voice change leaves Spanish lines alone unless asked: a catalog voice needs includeSpanish,
 * and a custom voice needs a Spanish voice string.
 */
export const shouldApplyBulkVoiceToDeployment = (
    dep: TelephonyDeploymentResponse,
    value: any
): boolean => {
    if (getVoiceLanguage(dep.locale) !== 'spanish') return true;
    if (!value || typeof value !== 'object') return false;

    return value.isCustom ? !!value.spanish : value.includeSpanish === true;
};

export const buildBulkVoiceUpdateRequest = (
    dep: TelephonyDeploymentResponse,
    value: any
): any => {
    let request: any = { ...dep };
    if (value && typeof value === 'object' && value.isCustom) {
        const isSpanish = (dep.locale || '').toLowerCase().startsWith('es');
        const voiceStr = isSpanish ? value.spanish : value.english;
        request.ttsVoice = voiceStr;
        request.ttsProvider = voiceStr ? (value.provider || 'ElevenLabs') : 'Deepgram';
        request.ttsOptions = [{ ttsProvider: request.ttsProvider, ttsVoice: voiceStr }];
    } else {
        // A catalog voice is passed by name, or as { value, prompt } to add a Gemini style prompt.
        // An explicit prompt (even an empty one) replaces a preset's default prompt.
        const catalogValue = value && typeof value === 'object' ? value.value : value;
        const hasExplicitPrompt = !!value && typeof value === 'object' && typeof value.prompt === 'string';
        const prompt = hasExplicitPrompt ? value.prompt : '';
        const standardOption = resolveCatalogVoiceOption(catalogValue);
        const provider = standardOption ? standardOption.provider : (catalogValue ? 'ElevenLabs' : 'Deepgram');
        const optionVoice = standardOption ? resolveVoiceForDeployment(standardOption, dep) : catalogValue;
        const voiceString = !standardOption ? catalogValue
            : hasExplicitPrompt && supportsVoicePrompt(standardOption.provider) ? setVoiceStringPrompt(optionVoice, prompt)
            : optionVoice;
        request.ttsVoice = voiceString;
        request.ttsProvider = provider;
        request.ttsOptions = [{ ttsProvider: provider, ttsVoice: voiceString }];
    }
    return request;
};

export const buildBulkLiveUpdateRequest = (app: ApplicationModel, value: any): UpdateApplicationRequest => {
    if (value?.status === 'clear-target-date') {
        const { launchDate, targetLaunchDate, ...appFieldsToUpdate } = app;
        return { ...appFieldsToUpdate, clearTargetLaunchDate: true };
    }

    let newTargetLaunchDate = app.targetLaunchDate;
    let nextLive = false;
    
    if (typeof value === 'object' && value !== null) {
        nextLive = value.status === 'live';
        if (value.status === 'draft' && value.targetLaunchDate && value.targetLaunchDate.length === 10) {
            const parts = value.targetLaunchDate.split('/');
            if (parts.length === 3) {
                newTargetLaunchDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}T00:00:00Z`).toISOString();
            }
        }
    } else {
        nextLive = value === 'live';
    }
    
    return {
        ...app,
        isLive: nextLive,
        targetLaunchDate: newTargetLaunchDate
    };
};

export const buildFeatureFlagUpdateRequest = (
    appFlag: any,
    masterFlag: any,
    modelValue?: string,
    promptValue?: string
): any => {
    const mergedSettings = (masterFlag.featureFlagSettings || []).map((masterSetting: any) => {
        const matchedAppSetting = appFlag?.featureFlagSettings?.find((s: any) => s.id === masterSetting.id);
        let v = matchedAppSetting?.value || masterSetting.value || '';
        if (masterSetting.id === FoodOrderingGenAIModelSettingId) {
            if (modelValue) v = modelValue;
        } else if (masterSetting.id === FoodOrderingGenAIPromptOverrideSettingId) {
            if (promptValue !== undefined) v = promptValue;
            if (v === '{{constant.base_ordering_prompt}}') {
                v = '{{constant.new_ordering_prompt}}';
            }
        }
        return { ...masterSetting, value: v };
    });
    
    return {
        featureFlagId: GenAIFoodOrdering,
        featureFlagSettings: mergedSettings
    };
};

export const buildClosedDaysUpdateRequest = (existingRaw: string | undefined | null, newDates: string[]): string => {
    let finalValue = existingRaw || 'none';
    if (newDates.length > 0) {
        const existingDatesList = existingRaw
            ? existingRaw.split(',').map(p => p.trim()).filter(Boolean).filter(p => p.toLowerCase() !== 'none')
            : [];
        const combinedDates = Array.from(new Set([...existingDatesList, ...newDates]));
        finalValue = combinedDates.join(', ');
    }
    return finalValue;
};

export const resolveExistingClosedDays = (
    appId: string,
    locationStatuses: Record<string, { disabledDaysRaw?: string | null }>,
    resolvedLocationApps: ApplicationModel[]
): string => {
    const status = locationStatuses[appId];
    if (status && status.disabledDaysRaw) {
        return status.disabledDaysRaw;
    }
    const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
    if (sharedApp) {
        const sharedStatus = locationStatuses[sharedApp.id!];
        if (sharedStatus && sharedStatus.disabledDaysRaw) {
            return sharedStatus.disabledDaysRaw;
        }
    }
    return '';
};

export const resolveGenAIProviderVar = (
    templatesResult: {
        resultType?: string;
        data?: Array<{
            templateFormFieldValues?: Array<{ associatedVariable?: string | null }>;
            dynamicFormFieldValuesCache?: Array<{ associatedVariable?: string | null }>;
        }>;
    } | null | undefined
): { genAIProviderVar: string; fieldExistsOnLocation: boolean } => {
    let genAIProviderVar = 'genAIProvider';
    let fieldExistsOnLocation = false;
    if (templatesResult?.resultType === 'Ok' && templatesResult.data) {
        for (const template of templatesResult.data) {
            const allFieldValues = [
                ...(template.templateFormFieldValues || []),
                ...(template.dynamicFormFieldValuesCache || [])
            ];
            const found = allFieldValues.find((fv: { associatedVariable?: string | null }) => {
                const assocVar = (fv.associatedVariable || '').trim();
                return assocVar === 'genAIProvider' || assocVar === '{genAIProvider}';
            });
            if (found) {
                genAIProviderVar = found.associatedVariable!.trim();
                fieldExistsOnLocation = true;
                break;
            }
        }
    }
    return { genAIProviderVar, fieldExistsOnLocation };
};
